// src/components/Map/ItineraryLayerData.tsx
import React, { useEffect, useMemo } from "react";
import { Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import PopupItinerary from "./PopupItinerary";
import IconMarker from "./IconItinerary";

export type ItineraryPoint = {
  LAT: number;
  LNG?: number;
  LON?: number;
  name?: string;
  start?: string;
  end?: string;
  SOG?: number;
  distance?: number;
  COG?: number;
  ALT?: number;
  ALARMS?: string;
  PRIVATE_MODE?: number;
};

interface Itinerary {
  id: number;
  start: string;
  end: string;
  name: string;
  distance: number;
  ALT: number;
  LAT: number;
  LNG: number;
  COG: number;
  SOG: number;
  ALARMS: string;
  PRIVATE_MODE: number;
}

export type ItineraryLayerDataProps = {
  points: ItineraryPoint[];
  visible?: boolean;
  color?: string;
  autoFit?: boolean;
};

const getLng = (p: ItineraryPoint) =>
  typeof p.LNG === "number" ? p.LNG : typeof p.LON === "number" ? p.LON : undefined;

const ItineraryLayerData: React.FC<ItineraryLayerDataProps> = ({
  points,
  visible = true,
  color = "#0ea5e9",
  autoFit = true,
}) => {
  const map = useMap();

  useEffect(() => {
    if (!visible || points.length === 0 || !autoFit) return;

    const latlngs = points
      .map(p => {
        const lng = getLng(p);
        return lng !== undefined ? L.latLng(p.LAT, lng) : null;
      })
      .filter(Boolean) as L.LatLng[];

    if (latlngs.length > 0) {
      map.fitBounds(L.latLngBounds(latlngs), { padding: [30, 30] });
    }
  }, [points, visible, map, autoFit]);

  const line = useMemo<[number, number][]>(
    () =>
      points
        .map(p => {
          const lng = getLng(p);
          return lng !== undefined ? [p.LAT, lng] : null;
        })
        .filter(Boolean) as [number, number][],
    [points]
  );

  if (!visible || line.length === 0) return null;

  const handleItemClick = (item: Itinerary) => {
    console.log(item);
  };

return (
    <>
      <Polyline positions={line} pathOptions={{ color, weight: 5, opacity: 0.8 }} />
      {points.map((p, i) => {
        const lng = getLng(p);
        if (lng === undefined) return null;

        const popupData: Itinerary = {
          id: i,
          start: p.start ?? (p as any).TIMESTAMP ?? "",
          end: p.end ?? (p as any).TIMESTAMP_END ?? "",
          name: p.name ?? (p.SOG && p.SOG > 0 ? "Moteur démarré" : "Moteur arrêté"),
          distance: p.distance ?? (p as any).GPSDIST ?? 0,
          ALT: p.ALT ?? 0,
          LAT: p.LAT,
          LNG: lng,
          COG: p.COG ?? 0,
          SOG: p.SOG ?? 0,
          ALARMS: p.ALARMS ?? "",
          PRIVATE_MODE: p.PRIVATE_MODE ?? 0,
        };

        return (
          <Marker
            key={popupData.id}
            position={[popupData.LAT, popupData.LNG]}
            icon={IconMarker(
              popupData.COG,
              popupData.SOG,
              popupData.name,
              () => handleItemClick(popupData)
            )}
          >
            <PopupItinerary itinerary={popupData} />
          </Marker>
        );
      })}
    </>
  );
};

export default ItineraryLayerData;
